import { useEffect, useMemo, useState } from "react";
import { api, ApiError, type HitsikuittausRivi, type Hitsikuittaukset, type Kayttaja, type Kohde, type KuittausRatkaisu, type TunnistettuLinja } from "./api";
import { naytaPvm } from "./pvm";
import { t, esim } from "./kieli";
import PohjaTuontiPaneeli from "./PohjaTuonti";
import { useKohdeSynkka } from "./Kohdekonteksti";

/* ═══════════════════════════════════════════════════════════
   Puuttuvien hitsien kuittaus (213, T445). Mobiili ensin.

   Työnjohto kuittaa isometrin hitsin kentällä: hitsattu (kuka, milloin, vaihe) · ei hitsattu · ei tällä linjalla ·
   punakynämuutos. ⛔ Kuittaus EI luo hitsiä Sampoon: hitsi tulee masterin kautta tuonnissa, ja kuittausrivi sidotaan
   siihen. Linjat alueittain (tunnuksen alku 100/110/…); alueen sisällä linjat, joilla on avoimia, ensin.
   From → To on line listin väite (linja_reitti), ei Sampon päätelmä.
   ═══════════════════════════════════════════════════════════ */

// Kieli seuraa käyttäjää (9.10.2026: pekilon kielipyyhkäisy löysi tästä näkymästä suomea englanninkieliselle).
// Nimet funktioina, jotta kielen vaihto näkyy ilman uudelleenlatausta.
const RATKAISUT: { arvo: KuittausRatkaisu; nimi: () => string }[] = [
  { arvo: "hitsattu", nimi: () => t("kuittaus.r.hitsattu") },
  // 215: tehty, mutta saumasta ei näe tekijää — hitsaaja ja päivä eivät pakollisia (Toni kävelee itse tabletilla).
  { arvo: "hitsattu_selvitettava", nimi: () => t("kuittaus.r.hitsattu_selvitettava") },
  { arvo: "ei_hitsattu", nimi: () => t("kuittaus.r.ei_hitsattu") },
  { arvo: "ei_talla_linjalla", nimi: () => t("kuittaus.r.ei_talla_linjalla") },
  { arvo: "punakyna", nimi: () => t("kuittaus.r.punakyna") },
];
const TILAT: Record<HitsikuittausRivi["tila"], () => string> = {
  puuttuu_masterista: () => t("kuittaus.t.puuttuu_masterista"),
  masterissa_ilman_lehtea: () => t("kuittaus.t.masterissa_ilman_lehtea"),
  kohdistus_epavarma: () => t("kuittaus.t.kohdistus_epavarma"),
  kunnossa: () => t("kuittaus.t.kunnossa"),
};
const avoin = (r: HitsikuittausRivi) => r.ratkaisu === null && r.tila !== "kunnossa";
const tanaan = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Helsinki" });

export default function Hitsikuittaus({ kayttaja }: { kayttaja: Kayttaja }) {
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  // Toni 15.10.2026 klo 18.15 kohta 2: sivun kohde seuraa yläpalkkia (protot: yläpalkissa 55563, sivulla 55555 — oletus oli k[0]).
  const [kohde, setKohde] = useState<number | "">("");
  const { ylapalkissa, synkattu } = useKohdeSynkka(kohde, setKohde, { kaikkiSallittu: false });
  // Tarrat vain instanssissa, jossa tarra-etuliite on asetettu (sama asetus kuin Tarra-kentällä, T333) — Toni 15.10. kohta 1.
  const [tarrat, setTarrat] = useState(false);
  useEffect(() => { api.hitsauslokiTilanne().then((x) => setTarrat(!!(x.tarra_etuliite ?? "").trim())).catch(() => setTarrat(false)); }, []);
  const [data, setData] = useState<Hitsikuittaukset | null>(null);
  const [alue, setAlue] = useState<string>("");
  const [auki, setAuki] = useState<string | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [kohteetHaettu, setKohteetHaettu] = useState(false);   // T685: tyhjä kohdelista sanotaan ääneen
  // Tablettinäkymä (pääChat, Tonin välittämä 9.10.2026): vaakasuunnassa isometri-PDF näkyy listan rinnalla eikä uudessa välilehdessä.
  const [pdf, setPdf] = useState<{ id: number; nimi: string } | null>(null);
  const saaTuoda = kayttaja.roolit.some((r) => r === "paakayttaja" || r === "suunnittelija");

  useEffect(() => {
    api.kohteet().then((k) => {
      setKohteet(k); setKohteetHaettu(true);
      setKohde((nyt) => (nyt === "" && k.length ? k[0]!.id : nyt));
    }).catch((e) => setVirhe(e instanceof ApiError ? e.message : t("kuittaus.virhe.kohteet")));
  }, []);

  async function lataa(id = kohde) {
    if (!id || !synkattu) return;
    try { setData(await api.hitsikuittaus(id)); setVirhe(null); }
    catch (e) { setVirhe(e instanceof ApiError ? e.message : t("kuittaus.virhe.haku")); }
  }
  useEffect(() => { void lataa(); }, [kohde, synkattu]); // eslint-disable-line

  const reitit = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const r of data?.reitit ?? []) {
      const x = `${r.mista ?? "?"} → ${r.mihin ?? "?"}`;
      m.set(r.tag, [...(m.get(r.tag) ?? []), x]);
    }
    return m;
  }, [data]);

  const linjat = useMemo(() => {
    const ryhmat = new Map<string, HitsikuittausRivi[]>();
    for (const r of data?.rivit ?? []) {
      if (alue && r.alue !== alue) continue;
      ryhmat.set(r.linja_tunnus, [...(ryhmat.get(r.linja_tunnus) ?? []), r]);
    }
    return [...ryhmat.entries()]
      .map(([tunnus, rivit]) => ({ tunnus, rivit, avoimia: rivit.filter(avoin).length, alue: rivit[0]!.alue }))
      .sort((a, b) => (b.avoimia > 0 ? 1 : 0) - (a.avoimia > 0 ? 1 : 0) || (a.alue ?? "").localeCompare(b.alue ?? "")
                      || a.tunnus.localeCompare(b.tunnus));
  }, [data, alue]);

  return (
    <div className="nakyma hitsikuittaus" data-nakyma-sisalto="kuittaus" data-sivun-kohde={kohde}>
      <h1>{t("kuittaus.otsikko")}</h1>
      <p className="selite">{t("kuittaus.selite")}</p>
      {virhe && <p className="virhe" role="alert">{virhe}</p>}
      {kohteetHaettu && kohteet.length === 0 && (
        <p role="status" data-ei-kohteita="" style={{ fontWeight: 600, borderLeft: "4px solid var(--varoitus)", paddingLeft: 10 }}>{t("kohde.ei_nakyvia")}</p>
      )}
      {!ylapalkissa && kohteet.length > 1 && (
        <label>{t("kuittaus.kohde")}{" "}
          <select value={kohde} onChange={(e) => setKohde(Number(e.target.value))}>
            {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} {k.nimi}</option>)}
          </select>
        </label>
      )}
      {data && (
        <div className="kuittaus__alueet" role="group" aria-label={t("kuittaus.alue")} data-kuittaus-alueet>
          <button type="button" className={alue === "" ? "nappi nappi--valittu" : "nappi"} onClick={() => setAlue("")}>
            {t("kuittaus.kaikki", { n: data.alueet.reduce((s, a) => s + a.avoimia, 0) })}
          </button>
          {data.alueet.map((a) => (
            <button key={a.alue ?? "-"} type="button" data-kuittaus-alue={a.alue ?? ""}
                    className={alue === (a.alue ?? "") ? "nappi nappi--valittu" : "nappi"} onClick={() => setAlue(a.alue ?? "")}>
              {t("kuittaus.aluenappi", { alue: a.alue ?? "–", avoimia: a.avoimia, linjoja: a.linjoja })}
            </button>
          ))}
        </div>
      )}
      {kohde !== "" && <Tunnista kohde={kohde} tarrat={tarrat} avaa={(tunnus) => { setAlue(""); setAuki(tunnus);
        setTimeout(() => document.querySelector(`[data-kuittaus-linja="${CSS.escape(tunnus)}"]`)?.scrollIntoView({ block: "start" }), 50); }}
        naytaPdf={setPdf} />}
      {data && linjat.length === 0 && <p className="selite">{alue ? t("kuittaus.ei_kuitattavia_alue", { alue }) : t("kuittaus.ei_kuitattavia")}</p>}
      <div className="kuittaus__runko">
      <ul className="kuittaus__linjat">
        {linjat.map((l) => (
          <li key={l.tunnus} className="kortti" data-kuittaus-linja={l.tunnus}>
            <button type="button" className="kuittaus__linjaotsikko" aria-expanded={auki === l.tunnus}
                    onClick={() => setAuki(auki === l.tunnus ? null : l.tunnus)}>
              <strong>{l.tunnus}</strong>
              <span className="selite"> · {t("kuittaus.linja_alue", { alue: l.alue ?? "–" })} · {l.avoimia > 0 ? t("kuittaus.avoinna", { n: l.avoimia }) : t("kuittaus.kuitattu")} / {l.rivit.length}</span>
              <br />
              <span data-kuittaus-reitti>{(reitit.get(l.tunnus) ?? [t("kuittaus.ei_reittia")]).join(" · ")}</span>
            </button>
            {auki === l.tunnus && (
              <ul className="kuittaus__rivit">
                {l.rivit.map((r) => <Rivi key={r.id} r={r} hitsaajat={data?.hitsaajat ?? []} valmis={() => void lataa()} naytaPdf={setPdf} />)}
              </ul>
            )}
          </li>
        ))}
      </ul>
      {/* Isometri rinnalla: näkyy vain leveällä vaakanäytöllä (tyylit.css), joten kuittauslomake ei koskaan peitä sitä. */}
      <aside className="kuittaus__isometri" data-kuittaus-isometripaneeli={pdf?.id ?? ""}>
        {pdf ? <><div className="selite mono">{pdf.nimi}</div>
                  <iframe title={pdf.nimi} src={api.liiteOsoite(pdf.id)} /></>
             : <p className="selite">{t("kuittaus.isometri_paneeli")}</p>}
      </aside>
      </div>
      {kohde && data && (
        <p><a className="nappi" href={api.hitsikuittausVientiUrl(kohde)} data-kuittaus-vienti>{t("kuittaus.vienti")}</a></p>
      )}
      {saaTuoda && (
        <PohjaTuontiPaneeli
          otsikko={t("kuittaus.tuonti_otsikko")}
          selite={t("kuittaus.tuonti_selite")}
          pohjaUrl={api.hitsikuittausPohjaUrl()}
          esto={kohde ? null : t("kuittaus.tuonti_esto")}
          tuo={(sisalto, tiedosto, vahvista, paivita) => api.tuoHitsikuittaus(Number(kohde), sisalto, tiedosto, vahvista, paivita)}
          paivitysTeksti={t("kuittaus.tuonti_paivita")}
          valmis={() => void lataa()} />
      )}
    </div>
  );
}

/** Leveä vaakanäyttö (tabletti 10–11"): isometri avataan rinnalle. Kapealla avataan uuteen välilehteen kuten ennen. */
const leveaVaaka = () => window.matchMedia("(min-width: 900px) and (orientation: landscape)").matches;

function Tunnista({ kohde, avaa, naytaPdf, tarrat }: { kohde: number; tarrat: boolean; avaa: (tunnus: string) => void; naytaPdf: (p: { id: number; nimi: string }) => void }) {
  const [q, setQ] = useState(""); const [alue, setAlue] = useState(""); const [dn, setDn] = useState("");
  const [spec, setSpec] = useState(""); const [media, setMedia] = useState("");
  const [tulos, setTulos] = useState<{ rivit: TunnistettuLinja[]; rajattu: boolean } | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  async function hae() {
    try { setTulos(await api.tunnistaLinja(kohde, { q, alue, dn, spec, media })); setVirhe(null); }
    catch (e) { setVirhe(e instanceof ApiError ? e.message : t("kuittaus.virhe.haku")); }
  }
  const tapa = (x: string) => x.split(",").map((s) => t(`kuittaus.tapa.${s}` as "kuittaus.tapa.tarra")).join(" + ");
  return (
    <section className="kortti kuittaus__tunnista" data-kuittaus-tunnista>
      <h2 className="eyebrow">{t("kuittaus.tunnista")}</h2>
      <form onSubmit={(e) => { e.preventDefault(); void hae(); }} className="kuittaus__tunnistalomake">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tarrat ? t("kuittaus.tunnista_ohje") : t("kuittaus.tunnista_ohje_ilman_tarraa")} aria-label={t("kuittaus.tunnista")}
               data-kuittaus-tunnista-haku autoComplete="off" />
        <label>{t("kuittaus.rajain_alue")}<select value={alue} onChange={(e) => setAlue(e.target.value)}>
          <option value="">–</option>
          {["100", "110", "120", "130", "140"].map((a) => <option key={a} value={a}>{a}</option>)}
        </select></label>
        <label>{t("kuittaus.rajain_dn")}<input value={dn} onChange={(e) => setDn(e.target.value)} placeholder={esim("100")} inputMode="decimal" /></label>
        <label>{t("kuittaus.rajain_spec")}<input value={spec} onChange={(e) => setSpec(e.target.value)} placeholder={esim("E16H1A")} /></label>
        <label>{t("kuittaus.rajain_media")}<input value={media} onChange={(e) => setMedia(e.target.value)} placeholder={esim("APR")} /></label>
        <button type="submit" className="nappi nappi--ensisijainen" data-kuittaus-tunnista-hae>{t("kuittaus.tunnista_hae")}</button>
      </form>
      {virhe && <p className="virhe" role="alert">{virhe}</p>}
      {tulos && (
        <>
          <p className="selite" data-kuittaus-tunnista-maara={tulos.rivit.length}>
            {tulos.rivit.length ? t("kuittaus.tunnista_tulokset", { n: tulos.rivit.length }) : t("kuittaus.tunnista_ei")}
            {tulos.rajattu ? ` · ${t("kuittaus.tunnista_rajattu")}` : ""}
          </p>
          <ul className="kuittaus__tunnistetut">
            {tulos.rivit.map((l) => (
              <li key={l.linja_id} data-kuittaus-tunnistettu={l.tunnus}>
                <strong>{l.tunnus}</strong> <span className="selite">({tapa(l.tapa)})</span>
                <div data-kuittaus-tunnistettu-reitti>{(l.reitit ?? []).map((r) => `${r.mista ?? "?"} → ${r.mihin ?? "?"}`).join(" · ") || t("kuittaus.ei_reittia")}</div>
                <div className="selite">{l.dn ? `DN ${l.dn}` : "DN –"} · {l.spec ?? "spec –"} · {l.media ?? "–"}{l.alue ? ` · ${t("kuittaus.linja_alue", { alue: l.alue })}` : ""}</div>
                <div className="kuittaus__tunnistetunnapit">
                  {l.kuittausriveja > 0
                    ? <button type="button" className="nappi" onClick={() => avaa(l.tunnus)} data-kuittaus-tunnista-avaa>{t("kuittaus.tunnista_avaa", { n: l.avoimia })}</button>
                    : <span className="selite">{t("kuittaus.tunnista_ei_kuittauksia")}</span>}
                  {l.isometri_liite_id && (
                    <a className="nappi" href={api.liiteOsoite(l.isometri_liite_id)} target="_blank" rel="noreferrer"
                       onClick={(e) => { if (leveaVaaka()) { e.preventDefault(); naytaPdf({ id: l.isometri_liite_id!, nimi: l.tunnus }); } }}>
                      {t("kuittaus.nayta_isometri")}{l.isometreja > 1 ? ` (1/${l.isometreja})` : ""}
                    </a>)}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function Rivi({ r, hitsaajat, valmis, naytaPdf }: { r: HitsikuittausRivi; hitsaajat: { koodi: string }[]; valmis: () => void;
                                                   naytaPdf: (p: { id: number; nimi: string }) => void }) {
  const [ratkaisu, setRatkaisu] = useState<KuittausRatkaisu | null>(r.ratkaisu);
  const [hitsaaja, setHitsaaja] = useState(r.hitsaaja_koodi ?? "");
  const [asentaja, setAsentaja] = useState(r.asentaja_koodi ?? "");
  const [paiva, setPaiva] = useState(r.paiva ?? tanaan());
  const [vaihe, setVaihe] = useState<"" | "esivalmiste" | "asennus" | "korjaus">(r.vaihe ?? "");
  const [huomio, setHuomio] = useState(r.kuittaus_huomio ?? "");
  const [tyo, setTyo] = useState(false);
  const [viesti, setViesti] = useState<string | null>(null);
  const listaId = `kuittaus-hitsaajat-${r.id}`;

  async function tallenna(peru = false) {
    setTyo(true); setViesti(null);
    try {
      await api.kuittaaHitsi(r.id, peru ? { ratkaisu: null } : {
        // Tekijä, päivä ja vaihe vain «hitsattu»-ratkaisulla: päiväkentän oletus on tänään, ja muilla ratkaisuilla (myös 215:n
        // «tekijä ja päivä selvitettävä») se olisi tallentunut keksittynä päivänä (havaittu 9.10.2026).
        ratkaisu, hitsaaja_koodi: ratkaisu === "hitsattu" ? hitsaaja || null : null, asentaja_koodi: ratkaisu === "hitsattu" ? asentaja || null : null,
        paiva: ratkaisu === "hitsattu" ? paiva : null, vaihe: ratkaisu === "hitsattu" ? vaihe || null : null, kuittaus_huomio: huomio || null });
      setViesti(peru ? t("kuittaus.peruttu") : t("kuittaus.tallennettu"));
      valmis();
    } catch (e) { setViesti(e instanceof ApiError ? e.message : t("kuittaus.virhe.tallennus")); }
    finally { setTyo(false); }
  }

  return (
    <li className="kuittaus__rivi" data-kuittaus-rivi={r.id}>
      <div>
        <strong>{t("kuittaus.hitsi", { nro: r.hitsinumero })}</strong>
        {/* 214: hitsi kohdistetaan isometriin (piirustusnumero + PDF); lehti on lisätieto. */}
        {r.isometri && (
          <div data-kuittaus-isometri={r.isometri}>
            <span className="mono">{r.isometri}</span>{" · "}
            {r.isometri_liite_id
              ? <a className="nappi" href={api.liiteOsoite(r.isometri_liite_id)} target="_blank" rel="noreferrer" data-kuittaus-pdf
                   onClick={(e) => { if (leveaVaaka()) { e.preventDefault(); naytaPdf({ id: r.isometri_liite_id!, nimi: r.isometri! }); } }}>
                  {t("kuittaus.nayta_isometri")}</a>
              : <span className="selite" data-kuittaus-pdf-puuttuu>{t("kuittaus.pdf_puuttuu")}</span>}
          </div>
        )}
        <span className="selite"> · {t("kuittaus.lehti", { lehti: r.lehti ?? "–" })}{r.dn ? ` · DN ${r.dn}` : ""} · {TILAT[r.tila]()}</span>
        {!r.isometri && (r.isometri_liite_id
          ? <> · <a href={api.liiteOsoite(r.isometri_liite_id)} target="_blank" rel="noreferrer">{t("kuittaus.isometri")}</a></>
          : r.isometri_tiedosto ? <span className="selite"> · {t("kuittaus.isometri_ei", { tiedosto: r.isometri_tiedosto })}</span> : null)}
        {/* Huomio = JSG:n syy, kun numero tai koko ei täsmää isometriin (pääChatin toimeksianto, Tonin välittämä 9.10.2026):
            linjakävelyn kohde, joten se näkyy varoituksena eikä selitteenä. */}
        {r.huomio && <p className="huom huom--varoitus" data-kuittaus-huomio><strong>⚠ {t("kuittaus.huomio_otsikko")}:</strong> {r.huomio}</p>}
        {r.ratkaisu && (
          <div data-kuittaus-tila="kuitattu">
            {t("kuittaus.kuitattu_tila", { ratkaisu: RATKAISUT.find((x) => x.arvo === r.ratkaisu)?.nimi() ?? r.ratkaisu })}
            {r.hitsaaja_koodi ? ` · ${r.hitsaaja_koodi}` : ""}{r.paiva ? ` · ${naytaPvm(r.paiva)}` : ""}
            {r.kuittaaja ? ` (${r.kuittaaja})` : ""}
          </div>
        )}
      </div>
      <fieldset className="kuittaus__lomake">
        <legend>{t("kuittaus.ratkaisu")}</legend>
        {RATKAISUT.map((x) => (
          <label key={x.arvo} className="kuittaus__valinta">
            <input type="radio" name={`ratkaisu-${r.id}`} value={x.arvo} checked={ratkaisu === x.arvo}
                   onChange={() => setRatkaisu(x.arvo)} data-kuittaus-ratkaisu={x.arvo} /> {x.nimi()}
          </label>
        ))}
        {ratkaisu === "hitsattu" && (
          <>
            <label>{t("kuittaus.hitsaaja")}
              <input list={listaId} value={hitsaaja} onChange={(e) => setHitsaaja(e.target.value)} data-kuittaus-hitsaaja />
            </label>
            <datalist id={listaId}>{hitsaajat.map((h) => <option key={h.koodi} value={h.koodi} />)}</datalist>
            <label>{t("kuittaus.asentaja")}
              <input list={listaId} value={asentaja} onChange={(e) => setAsentaja(e.target.value)} />
            </label>
            <label>{t("kuittaus.paiva")} <input type="date" value={paiva} onChange={(e) => setPaiva(e.target.value)} data-kuittaus-paiva /></label>
            <label>{t("kuittaus.vaihe")}
              <select value={vaihe} onChange={(e) => setVaihe(e.target.value as typeof vaihe)}>
                <option value="">–</option>
                <option value="esivalmiste">{t("kuittaus.v.esivalmiste")}</option>
                <option value="asennus">{t("kuittaus.v.asennus")}</option>
                <option value="korjaus">{t("kuittaus.v.korjaus")}</option>
              </select>
            </label>
          </>
        )}
        <label>{t("kuittaus.huomio")} <input value={huomio} onChange={(e) => setHuomio(e.target.value)} /></label>
        <div>
          <button type="button" className="nappi nappi--ensisijainen" disabled={tyo || !ratkaisu}
                  onClick={() => void tallenna()} data-kuittaus-tallenna>{t("kuittaus.tallenna")}</button>
          {r.ratkaisu && <button type="button" className="nappi" disabled={tyo} onClick={() => void tallenna(true)}>{t("kuittaus.peru")}</button>}
        </div>
        {viesti && <p className="selite" data-kuittaus-viesti>{viesti}</p>}
      </fieldset>
    </li>
  );
}
